import { useEffect, useState, type RefObject } from 'react';

/** Figma 812px 뷰 기준 — 이 정도 스크롤하면 배너 구간 진입으로 간주 */
const DESIGN_MAIN_HEIGHT = 812;
const DESIGN_SCROLL_HIDE_AT = 80;

function getScrollHideThreshold(scrollRoot: HTMLElement): number {
  const main = scrollRoot.querySelector('main');
  const mainHeight = main?.clientHeight ?? DESIGN_MAIN_HEIGHT;
  return (DESIGN_SCROLL_HIDE_AT / DESIGN_MAIN_HEIGHT) * mainHeight;
}

/**
 * scrollTop 기준 하단 슬롯 교차 상태.
 * true → 가맹점 표시 / false → 프로모 배너 표시 (서로 반대).
 */
export function useRestaurantScrollVisibility(
  scrollRootRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): boolean {
  const [visibleByScroll, setVisibleByScroll] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setVisibleByScroll(true);
      return;
    }

    const root = scrollRootRef.current;
    if (!root) return;

    const update = () => {
      const threshold = getScrollHideThreshold(root);
      setVisibleByScroll(root.scrollTop < threshold);
    };

    update();

    root.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(root);

    return () => {
      root.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    };
  }, [enabled, scrollRootRef]);

  return visibleByScroll;
}
